import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, Save, User, Percent, DollarSign } from 'lucide-react';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/currencyUtils';

interface Employee {
  id: number;
  name: string;
  email: string;
}

interface CommissionAdjustModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId: number;
  grossCommission: number;
  mode: 'move' | 'split';
  currentUserId?: number;
  currentUserName?: string;
  currentPercentage?: number;
  onSuccess: () => void;
}

const CommissionAdjustModal: React.FC<CommissionAdjustModalProps> = ({
  isOpen,
  onClose,
  transactionId,
  grossCommission,
  mode,
  currentUserId,
  currentUserName,
  currentPercentage,
  onSuccess,
}) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [percentage, setPercentage] = useState<string>(currentPercentage?.toString() || '');
  const [permanent, setPermanent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keep raw number (not pre-rounded) so formatCurrency controls precision.
  const calculatedAmount = percentage
    ? grossCommission * (parseFloat(percentage) / 100)
    : 0;

  const loadEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      const response = await apiService.getActiveEmployeesForCommission();
      if (response.data) {
        setEmployees(response.data);
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      setError('Failed to load employee list');
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Both modes need the employee list:
      // - Split mode: pick a NEW employee to share with
      // - Move mode: defaults to current employee (just change %), but user can
      //   also reassign the trade to a different salesman entirely
      loadEmployees();
      setError(null);
      setSelectedUserId(mode === 'move' ? (currentUserId ?? null) : null);
      setPercentage(currentPercentage?.toString() || '');
      setPermanent(false);
    }
  }, [isOpen, loadEmployees, currentPercentage, mode, currentUserId]);

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setError('Please select an employee');
      return;
    }

    const percentValue = parseFloat(percentage);
    if (isNaN(percentValue) || percentValue < 0 || percentValue > 100) {
      setError('Percentage must be between 0 and 100');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'move') {
        await apiService.moveCommission(transactionId, {
          from_user_id: currentUserId!,
          to_user_id: selectedUserId,
          percentage: percentValue,
          permanent,
        });
      } else {
        await apiService.splitCommission(transactionId, {
          user_id: selectedUserId,
          percentage: percentValue,
          permanent,
        });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${mode} commission`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-md p-6 my-8 text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {mode === 'move' ? 'Change Commission %' : 'Split Commission'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Transaction info */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center text-sm text-blue-700">
              <DollarSign className="w-4 h-4 mr-1" />
              <span className="font-medium">Gross Commission:</span>
              <span className="ml-2">{formatCurrency(grossCommission)}</span>
            </div>
          </div>

          {/* Employee selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              {mode === 'move' ? 'Salesman' : 'Add Employee'}
            </label>
            {loadingEmployees ? (
              <div className="text-sm text-gray-500">Loading employees...</div>
            ) : (
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select an employee</option>
                {employees.map((emp) => (
                  <option
                    key={emp.id}
                    value={emp.id}
                    // In Split mode, the current user can't be selected as the new split target.
                    // In Move mode, the current user IS the default and selectable (no change → just %).
                    disabled={mode === 'split' && emp.id === currentUserId}
                  >
                    {emp.name} ({emp.email})
                    {mode === 'move' && emp.id === currentUserId ? ' — current' : ''}
                  </option>
                ))}
              </select>
            )}
            {mode === 'move' && currentUserName && (
              <p className="mt-1 text-xs text-gray-500">
                Pre-selected: <span className="font-medium">{currentUserName}</span>. Change to a different salesman to reassign this trade.
              </p>
            )}
          </div>

          {/* Percentage input — dropdown of common values + Custom escape */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Percent className="w-4 h-4 inline mr-1" />
              Commission Percentage
            </label>
            <PercentageDropdown value={percentage} onChange={setPercentage} />
          </div>

          {/* Calculated amount preview */}
          <div className="mb-4 p-3 bg-green-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Calculated Commission:</span>
              <span className="font-semibold text-green-700">
                {formatCurrency(calculatedAmount)}
              </span>
            </div>
          </div>

          {/* Permanent checkbox */}
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={permanent}
                onChange={(e) => setPermanent(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Save as default for future transactions
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-500 ml-6">
              This will set the percentage as the default for this employee on this account.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !selectedUserId || !percentage}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {mode === 'move' ? 'Update Commission' : 'Add Split'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Dropdown of common commission percentages with a "Custom…" escape that
 * swaps to a numeric text input. Matches the legacy Yii UI which had a
 * preset dropdown — way faster than typing for the 95% case.
 */
const COMMON_PERCENTAGES = [5, 10, 12.5, 15, 20, 25, 30, 40, 50];

interface PercentageDropdownProps {
  value: string;
  onChange: (next: string) => void;
}

const PercentageDropdown: React.FC<PercentageDropdownProps> = ({ value, onChange }) => {
  const matchedPreset = (v: string): number | null => {
    if (v === '') return null;
    const num = parseFloat(v);
    if (isNaN(num)) return null;
    const found = COMMON_PERCENTAGES.find((p) => Math.abs(p - num) < 0.0001);
    return found ?? null;
  };
  const isCustomValue = (v: string): boolean => v !== '' && !isNaN(parseFloat(v)) && matchedPreset(v) === null;

  // Mode is purely user-controlled — toggled by Custom… / Use preset clicks.
  // We seed it from the initial value once, then never auto-flip on `value`
  // changes. Auto-flipping was painful while typing in Custom mode (typing
  // "10.25" briefly equals "10" which is a preset → mode flipped to preset
  // mid-edit and stole focus). The parent already remounts/resets the modal
  // when a new transaction is selected, so the seed is effectively per-open.
  const [mode, setMode] = useState<'preset' | 'custom'>(
    isCustomValue(value) ? 'custom' : 'preset'
  );

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === '__custom__') {
      setMode('custom');
      return;
    }
    onChange(v);
  };

  // In preset mode: normalize `value` to the canonical preset string so the
  // <select> shows the right option even if the value comes through as
  // "10.00" or "10.0" (the option keys are "5", "10", "12.5", etc.).
  // If the value isn't a preset at all, show '' so the dropdown isn't stuck
  // in an invalid state — the inline notice below offers an Edit link.
  const matched = matchedPreset(value);
  const selectValue = matched !== null ? String(matched) : '';

  return (
    <div className="space-y-2">
      {mode === 'preset' ? (
        <>
          <select
            value={selectValue}
            onChange={handleSelectChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
          >
            <option value="">Select a percentage…</option>
            {COMMON_PERCENTAGES.map((p) => (
              <option key={p} value={String(p)}>{p}%</option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          {isCustomValue(value) && (
            <p className="text-xs text-gray-500">
              Current value <span className="font-medium">{value}%</span> is not a preset.{' '}
              <button
                type="button"
                onClick={() => setMode('custom')}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Edit
              </button>
            </p>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter percentage (0-100)"
              autoFocus
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
          </div>
          <button
            type="button"
            onClick={() => setMode('preset')}
            className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
          >
            Use preset
          </button>
        </div>
      )}
    </div>
  );
};

export default CommissionAdjustModal;
